import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CriteriaDrawer } from '@/components/common/CriteriaDrawer';
import { CriteriaItem } from '@/components/common/CriteriaItem';
import { FieldRow } from '@/components/common/FieldRow';
import { PasswordToggleButton } from '@/components/common/PasswordToggleButton';
import { PhoneInputField } from '@/components/common/PhoneInputField';
import { RequiredAsterisk } from '@/components/common/RequiredAsterisk';
import { TermsModal } from '@/components/common/TermsModal';
import { cn } from '@/lib/utils';
import { getFieldProps } from '@/lib/formFieldProps';
import {
  validateUsername,
  validatePassword,
  validateConfirmPassword,
  allMet,
  isEmailValid,
  isPhoneValid,
} from '@/lib/validators';
import { ROUTES } from '@/constants/routes';
import { ROLES } from '@/constants/subjects';
import { ERROR_MESSAGES } from '@/constants/errorMessages';

const VALID_ROLE_IDS = ROLES.map((r) => r.id);

export function SignUpFormSection() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get('role');
  const initialRole = VALID_ROLE_IDS.includes(roleParam) ? roleParam : '';

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    role: initialRole,
    email: '',
    phone: '',
    username: '',
    password: '',
    confirmPassword: '',
  });

  const [touched, setTouched] = useState({});
  const [focused, setFocused] = useState({
    username: false,
    password: false,
    confirmPassword: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const usernameCriteria = validateUsername(formData.username);
  const passwordCriteria = validatePassword(formData.password);
  const confirmCriteria = validateConfirmPassword(
    formData.password,
    formData.confirmPassword
  );

  const usernameValid = allMet(usernameCriteria);
  const passwordValid = allMet(passwordCriteria);
  const confirmValid = allMet(confirmCriteria);

  const errors = {
    firstName: !formData.firstName.trim() ? ERROR_MESSAGES.firstName.required : null,
    lastName: !formData.lastName.trim() ? ERROR_MESSAGES.lastName.required : null,
    role: !formData.role ? ERROR_MESSAGES.role.required : null,
    email: !formData.email.trim()
      ? ERROR_MESSAGES.email.required
      : !isEmailValid(formData.email)
      ? ERROR_MESSAGES.email.invalid
      : null,
    phone: !formData.phone.trim()
      ? ERROR_MESSAGES.phone.required
      : !isPhoneValid(formData.phone)
      ? ERROR_MESSAGES.phone.invalid
      : null,
  };

  const isFormValid =
    !errors.firstName &&
    !errors.lastName &&
    !errors.role &&
    !errors.email &&
    !errors.phone &&
    usernameValid &&
    passwordValid &&
    confirmValid &&
    agreedToTerms;

  function updateField(field, value) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  function markTouched(field) {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }

  function markFocused(field, isFocus) {
    if (field in focused) {
      setFocused((prev) => ({ ...prev, [field]: isFocus }));
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    setTouched({
      firstName: true,
      lastName: true,
      role: true,
      email: true,
      phone: true,
      username: true,
      password: true,
      confirmPassword: true,
    });
    if (!isFormValid) return;
    navigate(ROUTES.chooseVerificationMethod, {
      state: {
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        firstName: formData.firstName,
        lastName: formData.lastName,
      },
    });
  }

  return (
    <section className="bg-slate-50 py-12 sm:py-16 lg:py-20">
      <div className="section-container">
        <div className="mx-auto max-w-2xl">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg shadow-slate-900/5 sm:p-8">
            <div className="text-center">
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
                Create your account
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Get started with Mind Mirth in less than a minute.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-7 space-y-4" noValidate>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FieldRow
                  id="signup-first-name"
                  label="First Name"
                  required
                  error={touched.firstName && errors.firstName}
                >
                  <Input
                    id="signup-first-name"
                    type="text"
                    {...getFieldProps('firstName', formData, updateField, markTouched)}
                    autoComplete="given-name"
                    className={cn(
                      '!h-11 cursor-text text-base',
                      touched.firstName && errors.firstName &&
                        'border-rose-400 focus-visible:ring-rose-400'
                    )}
                    aria-invalid={touched.firstName && !!errors.firstName}
                  />
                </FieldRow>

                <FieldRow
                  id="signup-last-name"
                  label="Last Name"
                  required
                  error={touched.lastName && errors.lastName}
                >
                  <Input
                    id="signup-last-name"
                    type="text"
                    {...getFieldProps('lastName', formData, updateField, markTouched)}
                    autoComplete="family-name"
                    className={cn(
                      '!h-11 cursor-text text-base',
                      touched.lastName && errors.lastName &&
                        'border-rose-400 focus-visible:ring-rose-400'
                    )}
                    aria-invalid={touched.lastName && !!errors.lastName}
                  />
                </FieldRow>
              </div>

              <FieldRow
                id="signup-role"
                label="Role"
                required
                error={touched.role && errors.role}
              >
                <Select
                  value={formData.role}
                  onValueChange={(value) => {
                    updateField('role', value);
                    markTouched('role');
                  }}
                >
                  <SelectTrigger
                    id="signup-role"
                    className={cn(
                      '!h-11 cursor-pointer text-base',
                      touched.role &&
                        errors.role &&
                        'border-rose-400 focus-visible:ring-rose-400'
                    )}
                    aria-invalid={touched.role && !!errors.role}
                  >
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem
                        key={role.id}
                        value={role.id}
                        className="cursor-pointer"
                      >
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldRow>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FieldRow
                  id="signup-email"
                  label="Email"
                  required
                  error={touched.email && errors.email}
                >
                  <Input
                    id="signup-email"
                    type="email"
                    {...getFieldProps('email', formData, updateField, markTouched)}
                    autoComplete="email"
                    className={cn(
                      '!h-11 cursor-text text-base',
                      touched.email && errors.email &&
                        'border-rose-400 focus-visible:ring-rose-400'
                    )}
                    aria-invalid={touched.email && !!errors.email}
                  />
                </FieldRow>

                <FieldRow
                  id="signup-phone"
                  label="Phone Number"
                  required
                  error={touched.phone && errors.phone}
                >
                  <PhoneInputField
                    id="signup-phone"
                    value={formData.phone}
                    onChange={(v) => updateField('phone', v)}
                    onBlur={() => markTouched('phone')}
                    error={touched.phone && !!errors.phone}
                  />
                </FieldRow>
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="signup-username"
                  className="block text-sm font-semibold text-slate-700"
                >
                  Username
                  <RequiredAsterisk />
                </label>
                <div className="relative">
                  <Input
                    id="signup-username"
                    type="text"
                    {...getFieldProps('username', formData, updateField, markTouched, {
                      onFocus: () => markFocused('username', true),
                      onBlur: () => markFocused('username', false),
                    })}
                    autoComplete="username"
                    className={cn(
                      '!h-11 cursor-text text-base',
                      usernameValid &&
                        'border-emerald-300 pr-11 focus-visible:ring-emerald-400'
                    )}
                  />
                  {usernameValid && (
                    <Check
                      className="pointer-events-none absolute right-3 top-1/2 size-5 -translate-y-1/2 text-emerald-600"
                      strokeWidth={3}
                      aria-hidden="true"
                    />
                  )}
                </div>
                <CriteriaDrawer isOpen={focused.username}>
                  <CriteriaItem
                    met={usernameCriteria.firstLetter}
                    label="First character must be a letter"
                  />
                  <CriteriaItem
                    met={usernameCriteria.lettersNumbersOnly}
                    label="Letters and numbers only"
                  />
                  <CriteriaItem
                    met={usernameCriteria.minLength}
                    label="Minimum 8 characters"
                  />
                  <CriteriaItem
                    met={usernameCriteria.maxLength}
                    label="Maximum 20 characters"
                  />
                  <CriteriaItem
                    met={usernameCriteria.available}
                    label="Username is available"
                  />
                </CriteriaDrawer>
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="signup-password"
                  className="block text-sm font-semibold text-slate-700"
                >
                  Password
                  <RequiredAsterisk />
                </label>
                <div className="relative">
                  <Input
                    id="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    {...getFieldProps('password', formData, updateField, markTouched, {
                      onFocus: () => markFocused('password', true),
                      onBlur: () => markFocused('password', false),
                    })}
                    autoComplete="new-password"
                    className={cn(
                      '!h-11 cursor-text pr-11 text-base',
                      passwordValid &&
                        'border-emerald-300 pr-20 focus-visible:ring-emerald-400'
                    )}
                  />
                  {passwordValid && (
                    <Check
                      className="pointer-events-none absolute right-11 top-1/2 size-5 -translate-y-1/2 text-emerald-600"
                      strokeWidth={3}
                      aria-hidden="true"
                    />
                  )}
                  <PasswordToggleButton
                    isVisible={showPassword}
                    onToggle={() => setShowPassword((s) => !s)}
                  />
                </div>
                <CriteriaDrawer isOpen={focused.password}>
                  <CriteriaItem
                    met={passwordCriteria.minLength}
                    label="Minimum 6 characters"
                  />
                  <CriteriaItem
                    met={passwordCriteria.hasLetter}
                    label="Contains at least one letter"
                  />
                  <CriteriaItem
                    met={passwordCriteria.hasNumber}
                    label="Contains at least one number"
                  />
                </CriteriaDrawer>
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="signup-confirm-password"
                  className="block text-sm font-semibold text-slate-700"
                >
                  Confirm Password
                  <RequiredAsterisk />
                </label>
                <div className="relative">
                  <Input
                    id="signup-confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...getFieldProps('confirmPassword', formData, updateField, markTouched, {
                      onFocus: () => markFocused('confirmPassword', true),
                      onBlur: () => markFocused('confirmPassword', false),
                    })}
                    autoComplete="new-password"
                    className={cn(
                      '!h-11 cursor-text pr-11 text-base',
                      confirmValid &&
                        'border-emerald-300 pr-20 focus-visible:ring-emerald-400'
                    )}
                  />
                  {confirmValid && (
                    <Check
                      className="pointer-events-none absolute right-11 top-1/2 size-5 -translate-y-1/2 text-emerald-600"
                      strokeWidth={3}
                      aria-hidden="true"
                    />
                  )}
                  <PasswordToggleButton
                    isVisible={showConfirmPassword}
                    onToggle={() => setShowConfirmPassword((s) => !s)}
                  />
                </div>
                <CriteriaDrawer isOpen={focused.confirmPassword}>
                  <CriteriaItem
                    met={confirmCriteria.match}
                    label="Passwords match"
                  />
                </CriteriaDrawer>
              </div>

              <label
                htmlFor="signup-terms"
                className="flex cursor-pointer select-none items-start gap-2 pt-2"
              >
                <input
                  id="signup-terms"
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setAgreedToTerms((prev) => !prev);
                    }
                  }}
                  className="mt-0.5 size-4 cursor-pointer rounded border-slate-300 text-brand-600 focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
                />
                <span className="text-sm text-slate-700">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setIsTermsModalOpen(true)}
                    className="cursor-pointer font-semibold text-brand-700 underline-offset-2 transition-colors hover:text-brand-800 hover:underline"
                  >
                    Terms and Conditions
                  </button>
                </span>
              </label>

              <button
                type="submit"
                disabled={!isFormValid}
                className={cn(
                  'w-full rounded-lg px-6 py-3 text-base font-semibold transition-all',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500',
                  isFormValid
                    ? 'cursor-pointer shadow-md hover:shadow-lg hover:brightness-95'
                    : 'cursor-not-allowed bg-slate-100 text-slate-400 ring-1 ring-inset ring-slate-200'
                )}
                style={
                  isFormValid
                    ? { backgroundColor: 'var(--color-brand-600)', color: '#ffffff' }
                    : undefined
                }
              >
                Sign Up
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-slate-600">
              Already have an account?{' '}
              <Link
                to={ROUTES.signIn}
                className="cursor-pointer font-semibold text-brand-700 underline-offset-2 transition-colors hover:text-brand-800 hover:underline"
              >
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>

      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />
    </section>
  );
}